"use client";

import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "../../lib/utils";
import {
  NestedSurfaceBg,
  resolveNestedSurfaceBg,
} from "../../lib/resolve-nested-surface-bg";

const FluidTabsInactiveTriggerBgContext =
  React.createContext<NestedSurfaceBg>("bg-muted");

const FluidTabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, forwardedRef) => {
  const localRef = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    const node = localRef.current;
    if (!node || !node.parentElement) return;
    // Reads from the Root (which has a real inherited background) rather
    // than the List itself: the List has -mb-px + z-10 to overlap the
    // content's top border, so giving the List its own background would
    // paint over that border across its full width, not just under the tabs.
    const parentBg = getComputedStyle(node.parentElement).backgroundColor;
    node.style.setProperty("--fluid-corner-bridge", parentBg);

    // Inactive tabs default to bg-muted, which is invisible when the
    // tablist itself sits on a bg-muted surface. Probe the real muted
    // color (rather than comparing to a hardcoded value) so this stays
    // correct across themes, then flag it for the trigger to react to.
    const probe = document.createElement("div");
    probe.className = "bg-muted";
    probe.style.cssText = "position:absolute;visibility:hidden;";
    node.parentElement.appendChild(probe);
    const mutedBg = getComputedStyle(probe).backgroundColor;
    node.parentElement.removeChild(probe);

    node.toggleAttribute("data-parent-muted", parentBg === mutedBg);
  });

  return (
    <TabsPrimitive.List
      ref={(node) => {
        localRef.current = node;
        if (typeof forwardedRef === "function") forwardedRef(node);
        // eslint-disable-next-line no-param-reassign
        else if (forwardedRef) forwardedRef.current = node;
      }}
      className={cn(
        "group/list relative z-10 h-14 -mb-px -space-x-px flex flex-row gap-0",
        className
      )}
      {...props}
    />
  );
});
FluidTabsList.displayName = "CardTabsList";

const FluidTabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, children, ...props }, ref) => {
  const inactiveTriggerBg = React.useContext(FluidTabsInactiveTriggerBgContext);

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        "group relative inline-flex -mb-px items-center justify-center h-full whitespace-nowrap text-sm font-medium",
        "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-50",
        // Inactive: fully rounded pill-card
        "px-2 pb-2 border border-transparent data-[state=active]:border-border rounded-t-lg",
        // Active: flat bottom, no bottom border, bg masks the content's top border
        "data-[state=active]:rounded-b-none data-[state=active]:border-b-transparent",
        "data-[state=active]:bg-background data-[state=active]:text-foreground",
        className
      )}
      {...props}
    >
      <div
        className={cn(
          "rounded-lg p-4 flex items-center size-full text-muted-foreground group-data-[state=active]:bg-background data-[state=active]:text-foreground",
          inactiveTriggerBg
        )}
      >
        {children}
      </div>
      {/*
      Corner bridges: positioned just outside the active tab's bottom-left/right edges.
      Outer square is bg-background (masks content behind it). Inner square is painted
      with --fluid-corner-bridge (set on FluidTabsList from the Root's computed background)
      + two borders (bottom + side) with a rounded inner corner, drawing the concave
      outward curve that connects the tab's side border to the content's top border.
    */}
      <div className="absolute bg-background -bottom-px -left-3 h-3 w-3 invisible group-first-of-type:hidden group-data-[state=active]:visible">
        <div
          aria-hidden="true"
          className="pointer-events-none size-full rounded-br-full border-b border-r border-border bg-[var(--fluid-corner-bridge)]"
        />
      </div>
      <div className="absolute bg-background -bottom-px -right-3 h-3 w-3 invisible group-data-[state=active]:visible">
        <div
          aria-hidden="true"
          className="pointer-events-none size-full rounded-bl-full border-b border-l border-border bg-[var(--fluid-corner-bridge)]"
        />
      </div>
    </TabsPrimitive.Trigger>
  );
});
FluidTabsTrigger.displayName = "CardTabsTrigger";

const FluidTabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "rounded-lg border border-border p-4",
      "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 p-4 rounded-lg",
      className
    )}
    {...props}
  />
));
FluidTabsContent.displayName = "CardTabsContent";

export type TabsFluidTab = {
  value: string;
  title: React.ReactNode;
  body: React.ReactNode;
};

interface TabsFluidProps extends React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.Root
> {
  tabs: TabsFluidTab[];
  listClassName?: string;
  triggerClassName?: string;
  contentClassName?: string;
}

function TabsFluid({
  tabs,
  defaultValue,
  value: valueProp,
  onValueChange,
  listClassName,
  triggerClassName,
  contentClassName,
  className,
  ...props
}: TabsFluidProps) {
  const initial = defaultValue ?? tabs[0]?.value;
  const [internalActive, setInternalActive] = React.useState(initial);
  const active = valueProp ?? internalActive;
  const isFirstActive = active === tabs[0]?.value;
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [inactiveTriggerBg, setInactiveTriggerBg] =
    React.useState<NestedSurfaceBg>("bg-muted");

  React.useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    setInactiveTriggerBg(resolveNestedSurfaceBg(root.parentElement));
  }, [className]);

  return (
    <FluidTabsInactiveTriggerBgContext.Provider value={inactiveTriggerBg}>
      <TabsPrimitive.Root
        ref={rootRef}
        defaultValue={initial}
        value={valueProp}
        onValueChange={(val) => {
          setInternalActive(val);
          onValueChange?.(val);
        }}
        {...props}
        className={cn(className, "bg-inherit")}
      >
        <FluidTabsList className={listClassName}>
          {tabs.map((tab) => (
            <FluidTabsTrigger
              key={tab.value}
              value={tab.value}
              className={triggerClassName}
            >
              {tab.title}
            </FluidTabsTrigger>
          ))}
        </FluidTabsList>
        {tabs.map((tab) => (
          <FluidTabsContent
            key={tab.value}
            value={tab.value}
            className={cn(isFirstActive && "rounded-tl-none", contentClassName)}
          >
            {tab.body}
          </FluidTabsContent>
        ))}
      </TabsPrimitive.Root>
    </FluidTabsInactiveTriggerBgContext.Provider>
  );
}

export { TabsFluid };
